import { Button } from "components/ui/button";
import { Calendar } from "components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import {
  format,
  isAfter,
  isBefore,
  isToday,
  startOfDay,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import React from "react";
import { cn } from "../../lib/utils";

interface SingleDatePickerProps extends React.HTMLAttributes<HTMLDivElement> {
  date?: Date | null;
  onDateChange?: (date: Date | null) => void;
  maxDate?: Date;
  minDate?: Date;
  placeholder?: string;
  buttonClassName?: string;
}

function SingleDatePicker({
  className,
  date: initialDate,
  onDateChange,
  maxDate,
  minDate,
  placeholder = "Selecione uma data",
  buttonClassName,
  ...props
}: SingleDatePickerProps) {
  const [date, setDate] = React.useState<Date | undefined>(initialDate || undefined);
  const [isOpen, setIsOpen] = React.useState(false);

  React.useEffect(() => {
    setDate(initialDate || undefined);
  }, [initialDate]);

  const handleSelect = (selectedDate: Date | undefined) => {
    setDate(selectedDate);
    if (onDateChange) {
      onDateChange(selectedDate || null);
    }
    setIsOpen(false);
  };

  const isFutureOrDisabled = (checkDate: Date) => {
    const today = startOfDay(new Date());
    const dateToCheck = startOfDay(checkDate);

    if (maxDate && isAfter(dateToCheck, startOfDay(maxDate))) {
      return true;
    }

    if (minDate && isBefore(dateToCheck, startOfDay(minDate))) {
      return true;
    }

    return isToday(dateToCheck) || isAfter(dateToCheck, today);
  };

  return (
    <div className={cn("grid gap-2", className)} {...props}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            id="date"
            className={cn(
              "w-[200px] justify-start text-left font-normal bg-[#2E0D1E] hover:bg-[#2E0D1E]/90 text-white transition-colors",
              buttonClassName
            )}
            onClick={() => setIsOpen(!isOpen)}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date ? (
              format(date, "dd/MM/yyyy")
            ) : (
              <span>{placeholder}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={date}
            onSelect={handleSelect}
            disabled={isFutureOrDisabled}
            defaultMonth={date}
            locale={ptBR}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default SingleDatePicker;
